import "../../../components/common/Tables/DataTable.css"
import React, {useState} from "react";
import { useSelector } from "react-redux";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Tooltip } from "@mui/material";
import RootState from "../../../redux/states/root.state";
import usePermissionCheck from "../../../hooks/usePermissionCheck";
import { DataTableFoot, SortButton } from "../../../components/common/Tables/DataTableParts";
import { ExternalLinkIcon, PencilIcon, TrashIcon } from "../../../components/common/Icons/Icons";

type Order = 'asc' | 'desc';
const headers = [
  {id:'name', label:'Name'},
  {id:'corporate', label:'Corporation'},
  {id:'country', label:'Country'},
  {id:'region', label:'Region'},
  {id:'city', label:'City'},
  {id:'url', label:'URL', align:'center'},
  {id:'supplies', label:'Supplies', align:'center'},
  {id:'edit', label:'Edit', align:'center'},
  {id:'delete', label:'Delete', align:'center'},
];

// Row actions pinned to the right edge (px, fits each heading)
const actionWidths: Record<string, number> = {
  edit: 60,
  delete: 68,
};

interface DealershipsTableProps {
  setDeleteOpen: any;
  setEditOpen: any;
  setDealershipToModify: any;
  // Page controls (filters toggle, Add Dealership) shown in the table's toolbar
  toolbar?: React.ReactNode;
}

const DealershipsTable: React.FC<DealershipsTableProps> = ({setDeleteOpen,setEditOpen,setDealershipToModify,toolbar}) => {
  const navigate = useNavigate();
  const {data:dealerships, loading:isLoading, meta} = useSelector((state: RootState) => state.dealerships);

  const [searchParams, setSearchParams] = useSearchParams();
  const {canAccess:canEditDealership} = usePermissionCheck(['update_dealership']);
  const {canAccess:canDeleteDealership} = usePermissionCheck(['delete_dealership']);

  // Table Sort (read from the URL so the arrow survives a reload)
  const [order, setOrder] = useState<Order>(searchParams.get('sort_direction') === 'desc' ? 'desc' : 'asc');
  const [orderBy, setOrderBy] = useState(searchParams.get('sort_by') ?? '');

  const page = searchParams.get("page") ? Number(searchParams.get("page")) : 1;
  const totalPages = meta?.total_pages ?? 1;
  const total = meta?.total ?? 0;

  const handleRequestSort = (header_id: string) => {
    const isAsc = orderBy === header_id && order === 'asc';
    const newOrder = isAsc ? 'desc' : 'asc';
    setOrder(newOrder);
    setOrderBy(header_id);
    searchParams.set("sort_direction", newOrder);
    searchParams.set("sort_by", header_id);
    setSearchParams(searchParams);
  };

  const goToPage = (value: number) => {
    searchParams.set("page", String(value));
    setSearchParams(searchParams);
  }

  // Edit / Delete only for those allowed (the old table kept an empty heading otherwise)
  const visibleHeaders = headers.filter((header)=>
    (header.id !== 'edit' || canEditDealership) && (header.id !== 'delete' || canDeleteDealership)
  );
  const pinned = visibleHeaders.map((header)=>header.id).filter((id)=>id in actionWidths);

  const pinStyle = (id: string) => {
    const index = pinned.indexOf(id);
    if (index < 0) return undefined;
    const right = pinned.slice(index + 1).reduce((sum, next) => sum + actionWidths[next], 0);
    return {'--dt-right': `${right}px`, '--dt-w': `${actionWidths[id]}px`} as React.CSSProperties;
  };

  const cellClass = (header: typeof headers[number]) => [
    header.align ? 'dt-center' : '',
    pinned.includes(header.id) ? 'dt-pin' : '',
    pinned[0] === header.id ? 'dt-pin-start' : '',
  ].filter(Boolean).join(' ') || undefined;

  const externalLink = (url: string | undefined, label: string) => url ? (
    <Tooltip title={url} arrow>
      <a href={url} className="dt-act" target="_blank" rel="noreferrer" aria-label={label}>
        <ExternalLinkIcon/>
      </a>
    </Tooltip>
  ) : <span className="dt-na">—</span>;

  const renderCell = (id: string, dealership: any) => {
    switch (id) {
      case 'name':
        return (
          <a
            href={`/dealership/${dealership?.id}`}
            className="dt-name"
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => {
              if (e.metaKey || e.ctrlKey || e.shiftKey) return;
              e.preventDefault();
              navigate(`/dealership/${dealership?.id}`);
            }}
          >
            {dealership?.name ?? "-"}
          </a>
        );

      case 'corporate':
        return dealership?.corporate?.name ?? "";

      case 'country':
        return dealership?.country?.label;

      case 'region':
        return dealership?.region?.label;

      case 'city':
        return dealership?.city ?? "";

      case 'url':
        return externalLink(dealership?.website_url, 'Open website');

      case 'supplies':
        return externalLink(dealership?.supplies_url, 'Open supplies site');

      case 'edit':
        return (
          <Tooltip title="Edit dealership" arrow>
            <button type="button" className="dt-act" onClick={()=>{setEditOpen(true);setDealershipToModify(dealership)}} aria-label="Edit dealership">
              <PencilIcon/>
            </button>
          </Tooltip>
        );

      case 'delete':
        return (
          <Tooltip title="Delete dealership" arrow>
            <button type="button" className="dt-act danger" onClick={()=>{setDeleteOpen(true);setDealershipToModify(dealership)}} aria-label="Delete dealership">
              <TrashIcon/>
            </button>
          </Tooltip>
        );

      default:
        return null;
    }
  };

  return (
    <div className="dt">
      {toolbar && <div className="dt-toolbar">
        <div className="dt-toolbar-start">{toolbar}</div>
      </div>}

      <div className="dt-scroll">
        {isLoading ? (
          <div className="dt-state"><span className="spinner" role="progressbar" aria-label="Loading dealerships"/></div>
        ) : dealerships?.length > 0 ? (
          <table className="dt-table">
            <thead>
              <tr>
                {visibleHeaders.map((header)=>(
                  <th
                    key={header.id}
                    scope="col"
                    className={cellClass(header)}
                    style={pinStyle(header.id)}
                    aria-sort={orderBy === header.id ? (order === 'asc' ? 'ascending' : 'descending') : undefined}
                  >
                    {header.id !== 'delete' && header.id !== 'edit' ? (
                      <SortButton label={header.label} active={orderBy === header.id} order={order} onClick={()=>handleRequestSort(header.id)}/>
                    ) : header.label}
                  </th>
                ))}
              </tr>
            </thead>

            <tbody>
              {dealerships.map((dealership:any, i:number)=>(
                <tr key={dealership?.id ?? i}>
                  {visibleHeaders.map((header)=>(
                    <td key={header.id} className={cellClass(header)} style={pinStyle(header.id)}>
                      {renderCell(header.id, dealership)}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <div className="dt-state">
            <div className="dt-empty-title">No Dealerships Found</div>
          </div>
        )}
      </div>

      <DataTableFoot
        shown={dealerships?.length ?? 0}
        total={total}
        noun={total === 1 ? 'Dealership' : 'Dealerships'}
        page={page}
        totalPages={totalPages}
        loading={isLoading}
        onPage={goToPage}
      />
    </div>
  );
};

export default DealershipsTable;
